// Zod schemas + derived types for all Paystack webhook events.
//
// The schemas are the source of truth; the exported types are inferred from
// them, so a schema and its type cannot drift apart.
//
// OPTIONALITY IS EVIDENCE-BASED, NOT TRANSCRIBED FROM PAYSTACK'S DOCS.
// A field is required here only if it is present in every one of the 236 real
// events stored in shared.paystack_events (production, 2026-05-01 → 2026-07-14).
// Requiring a field Paystack actually omits would dead-letter live billing
// events, so the audit was run per event type. What it showed:
//
//   - invoice.* NEVER carry a top-level `currency` (0/112 present) — the
//     currency fallback in the handlers exists precisely because of this.
//   - invoice.payment_failed omits transaction.currency in 3/20 cases.
//   - charge.success omits plan.plan_code in 19/98 (standalone, non-sub charges).
//   - subscription.disable / not_renew omit `authorization` in 1/8 and 1/3.
//   - subscription.* never carry paid_at / created_at / currency.
//   - charge.success never carries `createdAt` (it uses `created_at`).
//   - customer.email is present in all 236 events, so it is safe to require.
//
// `looseObject` keeps unrecognised keys rather than stripping them, so nothing
// is silently lost from the payloads we persist to paystack_events.

import { z } from "zod";

// ─── Shared sub-shapes ────────────────────────────────────────────────────────
//
// Required vs optional is decided by what the handlers actually depend on, NOT
// by transcribing Paystack's documented payload. Paystack omits keys freely
// across event types, and a field marked required here that Paystack drops in
// practice means dead-lettering a live billing event. So: fields the dispatcher
// reads and cannot proceed without are required; everything else is optional and
// passes through (mostly into processor_metadata as an opaque blob).
//
// `looseObject` keeps unrecognised keys instead of stripping them, so nothing is
// silently lost from the payloads we persist.

const webhookCustomerSchema = z.looseObject({
  // The dispatcher resolves accounts by email — the one field it truly needs.
  email: z.string(),
  customer_code: z.string().optional(),
  first_name: z.string().nullish(),
  id: z.number().optional(),
  international_format_phone: z.string().nullish(),
  last_name: z.string().nullish(),
  metadata: z.record(z.string(), z.unknown()).nullish(),
  phone: z.string().nullish(),
  risk_action: z.string().optional(),
});

// Stored wholesale into processor_metadata; no individual field is dereferenced,
// so nothing here is required.
const webhookAuthorizationSchema = z.looseObject({
  account_name: z.string().nullish(),
  authorization_code: z.string().optional(),
  bank: z.string().optional(),
  bin: z.string().optional(),
  brand: z.string().optional(),
  card_type: z.string().optional(),
  channel: z.string().optional(),
  country_code: z.string().optional(),
  exp_month: z.string().optional(),
  exp_year: z.string().optional(),
  last4: z.string().optional(),
  reusable: z.boolean().optional(),
  signature: z.string().optional(),
});

// Every field is optional. plan_code drives the event gate, but it is not always
// present: subscription events can carry a bare plan and still be resolved via
// subscription_code (the gate's fallback path). currency is nullable — Paystack
// sends an explicit null, and the handlers fall back to the subscription's
// currency when it is missing.
const webhookPlanSchema = z.looseObject({
  plan_code: z.string().optional(),
  amount: z.number().optional(),
  currency: z.string().nullish(),
  description: z.string().nullish(),
  id: z.number().optional(),
  interval: z.string().optional(),
  name: z.string().optional(),
  // Paystack sends these as 0/1 NUMBERS, not the booleans the old interface
  // declared. Nothing reads them, so accept either rather than reject the event.
  send_invoices: z.union([z.boolean(), z.number()]).optional(),
  send_sms: z.union([z.boolean(), z.number()]).optional(),
});

// subscription_code / status / next_payment_date are all present in every
// invoice event in production, and the renewal handlers dereference each of
// them unguarded.
const webhookSubscriptionRefSchema = z.looseObject({
  subscription_code: z.string(),
  status: z.string(),
  next_payment_date: z.string().nullable(),
  amount: z.number().optional(),
  cron_expression: z.string().optional(),
  email_token: z.string().optional(),
  open_invoice: z.string().nullish(),
});

// currency is optional: absent in 3/20 real invoice.payment_failed events, which
// is why the handlers fall back to the subscription's currency.
const webhookTransactionSchema = z.looseObject({
  amount: z.number().optional(),
  currency: z.string().nullish(),
  reference: z.string().optional(),
  status: z.string().optional(),
});

// Builds the { event, data } envelope for a single event type.
function paystackEvent<TEvent extends string, TData extends z.ZodTypeAny>(
  event: TEvent,
  data: TData
) {
  return z.object({ data, event: z.literal(event) });
}

// ─── charge.success ───────────────────────────────────────────────────────────

export const chargeSuccessEventSchema = paystackEvent(
  "charge.success",
  z.looseObject({
    // reference is the idempotency key (derivePaystackEventId).
    reference: z.string(),
    customer: webhookCustomerSchema,
    // amount / currency / status / created_at are written straight into the
    // payment_events row by the member-activation handler, so they are genuinely
    // required rather than merely documented.
    amount: z.number(),
    currency: z.string(),
    status: z.string(),
    created_at: z.string(),
    paid_at: z.string().nullable(),
    fees: z.number().nullable(),
    id: z.number().optional(),
    domain: z.string().optional(),
    message: z.string().nullish(),
    gateway_response: z.string().optional(),
    channel: z.string().optional(),
    // Null on real charge.success events (e.g. card-on-file renewals).
    ip_address: z.string().nullish(),
    // z.unknown() is nonoptional in Zod v4 (unlike v3), so this must be marked
    // optional explicitly — Paystack omits metadata on charges that carry none,
    // and requiring the key would dead-letter them.
    metadata: z.unknown().optional(),
    authorization: webhookAuthorizationSchema.optional(),
    // Empty object on one-off charges; populated on subscription charges. Not
    // webhookPlanSchema — plan_code is absent here on standalone charges.
    plan: z.record(z.string(), z.unknown()).optional(),
  })
);

// ─── invoice.* ────────────────────────────────────────────────────────────────

// invoice_code is the idempotency key; subscription identifies what to update.
// status / amount / paid_at / createdAt / customer / authorization / transaction
// are present in all 112 real invoice events and are written into payment_events.
// Note there is NO top-level `currency` — Paystack never sends one on invoices.
const invoiceDataShape = {
  invoice_code: z.string(),
  subscription: webhookSubscriptionRefSchema,
  status: z.string(),
  amount: z.number(),
  // paid_at is genuinely null on unpaid invoices (all 20 payment_failed events).
  // createdAt, despite the old interface marking it nullable, is never null in
  // production (0/130) and is written into payment_events.event_at.
  paid_at: z.string().nullable(),
  createdAt: z.string(),
  customer: webhookCustomerSchema,
  authorization: webhookAuthorizationSchema,
  transaction: webhookTransactionSchema,
  domain: z.string().optional(),
  period_start: z.string().optional(),
  period_end: z.string().optional(),
  // Paystack sends `paid` as a NUMBER on invoice events, not the boolean the old
  // hand-written interface claimed. Nothing reads it, so accept either.
  paid: z.union([z.boolean(), z.number()]).optional(),
  description: z.string().nullish(),
};

export const invoiceCreateEventSchema = paystackEvent(
  "invoice.create",
  z.looseObject(invoiceDataShape)
);

export const invoiceUpdateEventSchema = paystackEvent(
  "invoice.update",
  z.looseObject(invoiceDataShape)
);

export const invoicePaymentFailedEventSchema = paystackEvent(
  "invoice.payment_failed",
  z.looseObject(invoiceDataShape)
);

// ─── subscription.* ───────────────────────────────────────────────────────────

// subscription_code identifies the subscription; plan drives gate + plan lookup.
// customer is present in all real subscription events and subscription.create
// resolves the account via customer.email, so it is required.
// authorization is NOT: it is absent in 1/8 real disable and 1/3 not_renew events.
const subscriptionDataShape = {
  subscription_code: z.string(),
  plan: webhookPlanSchema,
  customer: webhookCustomerSchema,
  status: z.string(),
  amount: z.number(),
  // Never null in production (0/18), and written into payment_events.event_at.
  createdAt: z.string(),
  authorization: webhookAuthorizationSchema.optional(),
  cron_expression: z.string().optional(),
  next_payment_date: z.string().nullish(),
  open_invoice: z.string().nullish(),
  domain: z.string().optional(),
};

// subscription.create is the one event that provisions a subscription, and it is
// the only one that carries a complete payload: all 7 real events have a full
// authorization (code/last4/brand/exp), a full plan (code/name/interval), a
// customer_code and a non-null next_payment_date. The onboarding path
// dereferences all of these, so they are required here — and only here.
export const subscriptionCreateEventSchema = paystackEvent(
  "subscription.create",
  z.looseObject({
    ...subscriptionDataShape,
    next_payment_date: z.string(),
    customer: webhookCustomerSchema.extend({
      customer_code: z.string(),
      // Nullable but never absent — the onboarding path types these as
      // `string | null`, not `| undefined`.
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
    }),
    plan: webhookPlanSchema.extend({
      plan_code: z.string(),
      name: z.string(),
      interval: z.string(),
      // Present on every real subscription.create; onboarding types it as
      // optional-but-not-nullable.
      currency: z.string().optional(),
    }),
    authorization: webhookAuthorizationSchema.extend({
      authorization_code: z.string(),
      last4: z.string(),
      brand: z.string(),
      exp_month: z.string(),
      exp_year: z.string(),
    }),
    created_at: z.string().optional(),
  })
);

export const subscriptionNotRenewEventSchema = paystackEvent(
  "subscription.not_renew",
  z.looseObject({
    ...subscriptionDataShape,
    id: z.number().optional(),
    email_token: z.string().optional(),
    integration: z.number().optional(),
    invoices: z.array(z.unknown()).optional(),
    invoices_history: z.array(z.unknown()).optional(),
    invoice_limit: z.number().optional(),
    split_code: z.string().nullish(),
    most_recent_invoice: z.unknown().nullish(),
  })
);

export const subscriptionDisableEventSchema = paystackEvent(
  "subscription.disable",
  z.looseObject({
    ...subscriptionDataShape,
    email_token: z.string().optional(),
  })
);

// subscription.expiring_cards — data is an array, unlike every other event.
export const subscriptionExpiringCardsEventSchema = paystackEvent(
  "subscription.expiring_cards",
  z.array(
    z.looseObject({
      // Each entry's subscription_code is used to resolve and update the sub.
      subscription: z.looseObject({
        subscription_code: z.string(),
        id: z.number().optional(),
        amount: z.number().optional(),
        next_payment_date: z.string().optional(),
        plan: webhookPlanSchema.optional(),
      }),
      expiry_date: z.string().optional(),
      description: z.string().optional(),
      brand: z.string().optional(),
      customer: webhookCustomerSchema.optional(),
    })
  )
);

// subscription.transfer — shape is not modelled upstream; kept permissive rather
// than guessed at. Passthrough so the extra keys survive to the dead-letter row.
export const subscriptionTransferEventSchema = paystackEvent(
  "subscription.transfer",
  z.looseObject({
    authorization: webhookAuthorizationSchema.optional(),
    created_at: z.string().optional(),
    subscription_code: z.string().optional(),
  })
);

// ─── refund.* ─────────────────────────────────────────────────────────────────

const refundEventDataSchema = z.looseObject({
  // transaction_reference is the idempotency key (derivePaystackEventId).
  transaction_reference: z.string(),
  // The old interface declared this a string ("Paystack sends amount as a string
  // in refund events") — every real refund event actually sends a number. The
  // handler already coerces with Number(), so accept both.
  amount: z.union([z.string(), z.number()]).optional(),
  // Present on every real refund event and written into payment_events.currency.
  currency: z.string(),
  status: z.string(),
  customer: webhookCustomerSchema.optional(),
  domain: z.string().optional(),
  integration: z.number().optional(),
  processor: z.string().optional(),
  refund_reference: z.string().nullish(),
});

export const refundPendingEventSchema = paystackEvent(
  "refund.pending",
  refundEventDataSchema
);
export const refundProcessingEventSchema = paystackEvent(
  "refund.processing",
  refundEventDataSchema
);
export const refundProcessedEventSchema = paystackEvent(
  "refund.processed",
  refundEventDataSchema
);
export const refundFailedEventSchema = paystackEvent(
  "refund.failed",
  refundEventDataSchema
);

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * Per-event `data` schemas, keyed by event type.
 *
 * The dispatcher validates `data` against the entry for the event it is about to
 * handle. Deliberately NOT a discriminated union parsed at the route: an event
 * we cannot validate still needs to reach the dead-letter path with its event
 * type intact, and a union parse would drop it before the gate ever sees it.
 */
export const PAYSTACK_EVENT_DATA_SCHEMAS = {
  "charge.success": chargeSuccessEventSchema.shape.data,
  "invoice.create": invoiceCreateEventSchema.shape.data,
  "invoice.update": invoiceUpdateEventSchema.shape.data,
  "invoice.payment_failed": invoicePaymentFailedEventSchema.shape.data,
  "subscription.create": subscriptionCreateEventSchema.shape.data,
  "subscription.not_renew": subscriptionNotRenewEventSchema.shape.data,
  "subscription.disable": subscriptionDisableEventSchema.shape.data,
  "subscription.expiring_cards":
    subscriptionExpiringCardsEventSchema.shape.data,
  "subscription.transfer": subscriptionTransferEventSchema.shape.data,
  "refund.pending": refundPendingEventSchema.shape.data,
  "refund.processing": refundProcessingEventSchema.shape.data,
  "refund.processed": refundProcessedEventSchema.shape.data,
  "refund.failed": refundFailedEventSchema.shape.data,
} as const;

export type PaystackEventType = keyof typeof PAYSTACK_EVENT_DATA_SCHEMAS;

export function isKnownPaystackEventType(
  event: string
): event is PaystackEventType {
  return event in PAYSTACK_EVENT_DATA_SCHEMAS;
}

/**
 * The envelope every Paystack webhook shares. This is all the route parses:
 * enough to identify the event and dead-letter it, without asserting anything
 * about `data`.
 *
 * `data` is nullish, not just unknown: in Zod v4 a bare z.unknown() requires the
 * key, which would 400 a body missing `data` at the route and stop it ever
 * reaching the dead-letter path — the opposite of what this envelope is for. A
 * present `event` is the only thing genuinely required to identify and store an
 * unrecognised event.
 */
export const paystackEnvelopeSchema = z.object({
  data: z.unknown().nullish(),
  event: z.string(),
});

export type PaystackEnvelope = z.infer<typeof paystackEnvelopeSchema>;

// ─── Derived types ────────────────────────────────────────────────────────────
// Names and shapes match the hand-written interfaces these schemas replaced, so
// every existing import site keeps compiling.

export interface PaystackWebhookEvent<TEvent extends string, TData> {
  data: TData;
  event: TEvent;
}

export type ChargeSuccessEvent = z.infer<typeof chargeSuccessEventSchema>;
export type InvoiceCreateEvent = z.infer<typeof invoiceCreateEventSchema>;
export type InvoiceUpdateEvent = z.infer<typeof invoiceUpdateEventSchema>;
export type InvoicePaymentFailedEvent = z.infer<
  typeof invoicePaymentFailedEventSchema
>;
export type SubscriptionCreateEvent = z.infer<
  typeof subscriptionCreateEventSchema
>;
export type SubscriptionNotRenewEvent = z.infer<
  typeof subscriptionNotRenewEventSchema
>;
export type SubscriptionDisableEvent = z.infer<
  typeof subscriptionDisableEventSchema
>;
export type SubscriptionExpiringCardsEvent = z.infer<
  typeof subscriptionExpiringCardsEventSchema
>;
export type SubscriptionTransferEvent = z.infer<
  typeof subscriptionTransferEventSchema
>;
export type RefundPendingEvent = z.infer<typeof refundPendingEventSchema>;
export type RefundProcessingEvent = z.infer<typeof refundProcessingEventSchema>;
export type RefundProcessedEvent = z.infer<typeof refundProcessedEventSchema>;
export type RefundFailedEvent = z.infer<typeof refundFailedEventSchema>;

// Discriminated union of all known Paystack events
export type AnyPaystackEvent =
  | ChargeSuccessEvent
  | InvoiceCreateEvent
  | InvoiceUpdateEvent
  | InvoicePaymentFailedEvent
  | SubscriptionCreateEvent
  | SubscriptionNotRenewEvent
  | SubscriptionDisableEvent
  | SubscriptionExpiringCardsEvent
  | RefundPendingEvent
  | RefundProcessingEvent
  | RefundProcessedEvent
  | RefundFailedEvent
  | SubscriptionTransferEvent;
